/**
 * Folders of a preset library, as the presets browser shows them: plain functions over
 * {@link PresetLibrary} (paths like `"Backend/Rust"`, `""` for the top), no state.
 */
import type { Preset } from "@protocol/Preset";
import type { PresetLibrary } from "@protocol/PresetLibrary";
import { shortValues } from "./fields";

/** The last part of a folder path (`"Backend/Rust"` → `"Rust"`). */
export function folderName(path: string): string {
  return path.slice(path.lastIndexOf("/") + 1);
}

/** The folder a path is in (`"Backend/Rust"` → `"Backend"`, `"Backend"` → `""`). */
export function parentFolder(path: string): string {
  const at = path.lastIndexOf("/");
  return at < 0 ? "" : path.slice(0, at);
}

/** `name` inside `parent`, cleaned like the core does (parts trimmed, empty ones dropped). */
export function joinFolder(parent: string, name: string): string {
  return `${parent}/${name}`
    .split("/")
    .map((part) => part.trim())
    .filter(Boolean)
    .join("/");
}

/** Whether `path` is `folder` or inside it (`""` holds everything). */
export function isWithin(path: string, folder: string): boolean {
  return !folder || path === folder || path.startsWith(`${folder}/`);
}

/** The folders directly inside `parent`, by name. */
export function childFolders(library: PresetLibrary, parent: string): string[] {
  return library.folders.filter((f) => parentFolder(f) === parent).sort((a, b) => folderName(a).localeCompare(folderName(b)));
}

/** The way from the top to `path`: each folder with its name (`"A/B"` → `A`, `A/B`). */
export function crumbs(path: string): { name: string; path: string }[] {
  if (!path) return [];
  const parts = path.split("/");
  return parts.map((name, i) => ({ name, path: parts.slice(0, i + 1).join("/") }));
}

/** How many presets are in `folder` or below it. */
export function countWithin(library: PresetLibrary, folder: string): number {
  return library.presets.filter((p) => isWithin(p.folder, folder)).length;
}

/** What the browser shows: the presets matching a text, or every preset. */
export interface PresetFilter {
  /** Words to look for in the label, node name, kind, short values (a language) and categories (lower case; empty: all). */
  query: string;
}

/** Whether `preset` fits `filter`. */
export function fits(preset: Preset, filter: PresetFilter): boolean {
  return !filter.query || `${preset.label} ${preset.name} ${preset.kind} ${shortValues(preset).join(" ")} ${preset.categories.join(" ")}`.toLowerCase().includes(filter.query);
}

/**
 * The presets shown for `folder`: those directly in it, or while searching, every match in
 * it and below (so a search finds presets in sub-folders too).
 */
export function presetsShown(library: PresetLibrary, folder: string, filter: PresetFilter): Preset[] {
  const searching = !!filter.query;
  return library.presets.filter((p) => (searching ? isWithin(p.folder, folder) : p.folder === folder) && fits(p, filter));
}

/** A copy of `preset` to save in `library` under `folder`: its id kept when free there, else a new one. */
export function copyInto(preset: Preset, library: PresetLibrary, folder: string): Preset {
  const taken = library.presets.some((p) => p.id === preset.id);
  return { ...structuredClone(preset), id: taken ? "" : preset.id, folder };
}
